import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApi } from '../../shared/hooks/useApi'
import { formatDate, formatDateTime } from '../../shared/utils/formatters'
import { Eye, Calendar, Clock, AlertTriangle, X, FileText, Zap, Sparkles } from 'lucide-react'
import {
  differenceInDays,
  differenceInHours,
  differenceInBusinessDays,
  parseISO,
  isPast,
} from 'date-fns'
import CPVBrowser from '../../shared/components/cpv/CPVBrowser'

interface TenderFilters {
  cpv_filters?: string[]
  deadline_filter?: string | null
  order_object?: string
}

interface Tender {
  id: string
  bzp_number?: string
  notice_number?: string
  organization_name: string
  order_object: string
  cpv_code?: string
  publication_date: string
  submitting_offers_date: string
}

interface DeadlineTimerProps {
  date: string | Date
}

function DeadlineTimer({ date }: DeadlineTimerProps) {
  if (!date) return null

  const targetDate = typeof date === 'string' ? parseISO(date) : date
  const now = new Date()
  const isExpired = isPast(targetDate)

  if (isExpired) {
    return (
      <div className="bg-surface-raised rounded-lg border border-border-subtle p-6 col-span-1 md:col-span-2">
        <div className="flex items-center">
          <div className="p-2 rounded-lg bg-surface text-ink-muted mr-4">
            <Calendar className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-ink-secondary">Pozostały czas</p>
            <p className="text-2xl font-semibold text-ink">Termin minął</p>
          </div>
        </div>
      </div>
    )
  }

  const days = differenceInDays(targetDate, now)
  const businessDays = differenceInBusinessDays(targetDate, now)
  const hours = differenceInHours(targetDate, now) % 24

  // Map urgency levels to StatCard color scheme
  let color: 'blue' | 'green' | 'purple' | 'orange' = 'green' // Default
  let icon = Clock

  if (businessDays < 3) {
    color = 'orange' // Most urgent
    icon = AlertTriangle
  } else if (businessDays < 5) {
    color = 'orange'
    icon = AlertTriangle
  } else if (businessDays < 10) {
    color = 'blue'
    icon = Clock
  }

  const colorClasses = {
    blue: 'bg-brand-bg text-brand',
    green: 'bg-success/5 text-success',
    purple: 'bg-ai/5 text-ai',
    orange: 'bg-warning/5 text-warning',
  }

  const IconComponent = icon

  return (
    <div className="bg-surface-raised rounded-lg border border-border-subtle p-6 col-span-1 md:col-span-2">
      <div className="flex items-center">
        <div className={`p-2 rounded-lg ${colorClasses[color]} mr-4`}>
          <IconComponent className="h-6 w-6" />
        </div>
        <div>
          <p className="text-sm font-medium text-ink-secondary">Pozostało czasu</p>
          <p className="text-2xl font-semibold text-ink">
            {days} dni {hours} godz.
          </p>
        </div>
      </div>
    </div>
  )
}

interface OpenTenderCardProps {
  tender: Tender
  onViewDetail: (id: string) => void
}

function OpenTenderCard({ tender, onViewDetail }: OpenTenderCardProps) {
  const navigate = useNavigate()
  const bzpNumber = tender.bzp_number
    ? `2025/BZP ${tender.bzp_number}`
    : tender.notice_number || `#${tender.id}`

  return (
    <div className="bg-surface-raised border border-border rounded-lg p-4 hover:border-border transition-colors">
      {/* Header line: Organization name + BZP number + Eye icon */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex-1 min-w-0">
          <span className="text-base font-semibold text-ink truncate">
            {tender.organization_name}
          </span>
        </div>
        <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
          <span className="text-sm text-ink-secondary bg-surface-inset px-2 py-1 rounded">{bzpNumber}</span>
          <button
            onClick={() => onViewDetail(tender.id)}
            className="text-brand hover:text-brand p-1"
            title="Zobacz szczegóły"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Order object */}
      <div className="mb-3">
        <div className="text-xs text-ink-muted uppercase tracking-wider font-semibold mb-1">
          PRZEDMIOT ZAMÓWIENIA
        </div>
        <p className="text-sm text-ink leading-relaxed">{tender.order_object}</p>
        {tender.cpv_code && (
          <div className="text-xs text-ink-muted mt-1">CPV: {tender.cpv_code}</div>
        )}
      </div>

      {/* Date grid: Publication + Deadline + Timer (matching NoticeDetail.jsx) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Publication Date */}
        <div className="flex items-center p-4 bg-surface-raised rounded-lg border border-border md:col-span-1">
          <div className="bg-brand-bg p-2 rounded-lg mr-4 flex items-center justify-center">
            <Calendar className="h-6 w-6 text-brand" />
          </div>
          <div>
            <div className="text-xs text-ink-muted font-semibold uppercase tracking-wide mb-1">
              Opublikowano
            </div>
            <div className="text-base font-bold text-ink">
              {formatDate(tender.publication_date)}
            </div>
          </div>
        </div>

        {/* Deadline Date */}
        <div className="flex items-center p-4 bg-surface-raised rounded-lg border border-border md:col-span-1">
          <div className="bg-warning/5 p-2 rounded-lg mr-4 flex items-center justify-center">
            <Calendar className="h-6 w-6 text-warning" />
          </div>
          <div>
            <div className="text-xs text-ink-muted font-semibold uppercase tracking-wide mb-1">
              Termin składania
            </div>
            <div className="text-base font-bold text-ink">
              {formatDateTime(tender.submitting_offers_date)}
            </div>
          </div>
        </div>

        {/* Timer (Double Width) */}
        <DeadlineTimer date={tender.submitting_offers_date} />
      </div>
    </div>
  )
}

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null

  const getPageNumbers = () => {
    const pages = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, 5)
      } else if (currentPage >= totalPages - 2) {
        pages.push(totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
      } else {
        pages.push(currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2)
      }
    }

    return pages
  }

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-surface-raised border-t border-border sm:px-6">
      <div className="flex justify-between flex-1 sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-ink-secondary bg-surface-raised border border-border rounded-md hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Poprzednia
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-ink-secondary bg-surface-raised border border-border rounded-md hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Następna
        </button>
      </div>

      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-ink-secondary">
            Strona <span className="font-medium">{currentPage}</span> z{' '}
            <span className="font-medium">{totalPages}</span>
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md -space-x-px">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-border bg-surface-raised text-sm font-medium text-ink-muted hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Poprzednia
            </button>

            {getPageNumbers().map((page) => (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  page === currentPage
                    ? 'z-10 bg-brand-bg border-brand text-brand'
                    : 'bg-surface-raised border-border text-ink-muted hover:bg-surface-hover'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-border bg-surface-raised text-sm font-medium text-ink-muted hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Następna
            </button>
          </nav>
        </div>
      </div>
    </div>
  )
}

interface SummaryCardsProps {
  stats: {
    total: number
    urgent: number
    new: number
  } | null
  activeFilter: string | null
  onFilterChange: (filter: string | null) => void
}

function SummaryCards({ stats, activeFilter, onFilterChange }: SummaryCardsProps) {
  if (!stats) return null

  const colorClasses = {
    blue: 'bg-brand-bg text-brand',
    green: 'bg-success/5 text-success',
    purple: 'bg-ai/5 text-ai',
    orange: 'bg-warning/5 text-warning',
    red: 'bg-danger/5 text-danger',
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {/* All Tenders */}
      <button
        onClick={() => onFilterChange(null)}
        className={`bg-surface-raised rounded-lg border border-border-subtle p-6 text-left transition-colors ${
          activeFilter === null ? 'ring-2 ring-brand' : 'hover:border-border'
        }`}
      >
        <div className="flex items-center">
          <div className={`p-2 rounded-lg ${colorClasses.blue} mr-4`}>
            <FileText className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-ink-secondary">WSZYSTKIE otwarte postępowania</p>
            <p className="text-2xl font-semibold text-ink">
              {stats.total?.toLocaleString('pl-PL')}
            </p>
          </div>
        </div>
      </button>

      {/* Urgent Tenders */}
      <button
        onClick={() => onFilterChange('urgent')}
        className={`bg-surface-raised rounded-lg border border-border-subtle p-6 text-left transition-colors ${
          activeFilter === 'urgent' ? 'ring-2 ring-danger' : 'hover:border-border'
        }`}
      >
        <div className="flex items-center">
          <div className={`p-2 rounded-lg ${colorClasses.red} mr-4`}>
            <Zap className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-ink-secondary">
              PILNE ({String.fromCharCode(60)} 5 dni roboczych)
            </p>
            <p className="text-2xl font-semibold text-ink">
              {stats.urgent?.toLocaleString('pl-PL')}
            </p>
          </div>
        </div>
      </button>

      {/* New Tenders */}
      <button
        onClick={() => onFilterChange('new')}
        className={`bg-surface-raised rounded-lg border border-border-subtle p-6 text-left transition-colors ${
          activeFilter === 'new' ? 'ring-2 ring-success' : 'hover:border-border'
        }`}
      >
        <div className="flex items-center">
          <div className={`p-2 rounded-lg ${colorClasses.green} mr-4`}>
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-ink-secondary">NOWE (ostatnie 7 dni)</p>
            <p className="text-2xl font-semibold text-ink">
              {stats.new?.toLocaleString('pl-PL')}
            </p>
          </div>
        </div>
      </button>
    </div>
  )
}

export default function OpenTendersPage({
  onViewNotice,
}: { onViewNotice?: (id: string) => void } = {}) {
  const navigate = useNavigate()
  const { getOpenTenders, getOpenTendersStats, loading, error } = useApi()

  const [tenders, setTenders] = useState<Tender[]>([])
  const [stats, setStats] = useState<{
    total: number
    urgent: number
    new: number
  } | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
  })
  const [selectedCPVCodes, setSelectedCPVCodes] = useState<string[]>([])
  const [activeFilter, setActiveFilter] = useState<string | null>(null) // null, 'urgent', 'new'
  const [orderObjectFilter, setOrderObjectFilter] = useState('')

  const loadStats = async () => {
    try {
      const statsData = await getOpenTendersStats({
        cpv_filters: selectedCPVCodes.length > 0 ? selectedCPVCodes : undefined,
        order_object: orderObjectFilter.trim() || undefined,
      })
      setStats(statsData)
    } catch (err) {
      console.error('Failed to load open tenders stats:', err)
    }
  }

  const loadTenders = async (page = 1, appliedFilters: TenderFilters = {}) => {
    try {
      const params = {
        page,
        page_size: pagination.page_size,
        ...appliedFilters,
      }

      const response = await getOpenTenders(params)
      setTenders(response.items || [])
      setPagination({
        page: response.page,
        page_size: response.page_size,
        total: response.total,
      })
    } catch (err) {
      console.error('Failed to load open tenders:', err)
    }
  }

  // Stats only refresh when CPV or keywords change
  useEffect(() => {
    loadStats()
  }, [selectedCPVCodes, orderObjectFilter])

  // Tenders refresh when any filter changes
  useEffect(() => {
    loadTenders(1, {
      cpv_filters: selectedCPVCodes.length > 0 ? selectedCPVCodes : undefined,
      deadline_filter: activeFilter,
      order_object: orderObjectFilter.trim() || undefined,
    })
  }, [selectedCPVCodes, activeFilter, orderObjectFilter])

  const handlePageChange = (newPage: number) => {
    loadTenders(newPage, {
      cpv_filters: selectedCPVCodes.length > 0 ? selectedCPVCodes : undefined,
      deadline_filter: activeFilter,
      order_object: orderObjectFilter.trim() || undefined,
    })
  }

  const handleFilterChange = (filterType: string | null) => {
    setActiveFilter(filterType)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleCPVSelectionChange = (selectedCodes: string[]) => {
    setSelectedCPVCodes(selectedCodes)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleClearFilters = () => {
    setSelectedCPVCodes([])
    setActiveFilter(null)
    setOrderObjectFilter('')
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const totalPages = Math.ceil(pagination.total / pagination.page_size)

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-ink">Otwarte postępowania</h2>
        <p className="text-ink-secondary mt-1">
          Ogłoszenia o zamówieniu z aktywnym terminem składania ofert
        </p>
      </div>

      {/* Summary Cards */}
      <SummaryCards stats={stats} activeFilter={activeFilter} onFilterChange={handleFilterChange} />

      {/* Filters Section */}
      <div className="bg-surface-raised border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-ink mb-4">Filtry wyszukiwania</h3>

        <div className="space-y-6">
          {/* CPV Browser */}
          <CPVBrowser
            selectedCodes={selectedCPVCodes}
            onSelectionChange={handleCPVSelectionChange}
            maxSelection={50}
          />

          {/* Order Object Filter */}
          <div>
            <label
              htmlFor="order-object-filter"
              className="block text-sm font-medium text-ink-secondary mb-2"
            >
              Przedmiot zamówienia
            </label>
            <input
              type="text"
              id="order-object-filter"
              value={orderObjectFilter}
              onChange={(e) => setOrderObjectFilter(e.target.value)}
              placeholder="Słowa kluczowe..."
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
            />
          </div>
        </div>
      </div>

      {/* Clear Filters Button */}
      {(selectedCPVCodes.length > 0 || activeFilter || orderObjectFilter.trim()) && (
        <div className="flex justify-end">
          <button
            onClick={handleClearFilters}
            className="flex items-center px-3 py-2 text-sm font-medium text-ink-secondary bg-surface-raised border border-border rounded-md hover:bg-surface-hover"
          >
            <X className="h-4 w-4 mr-1" />
            Wyczyść filtry
          </button>
        </div>
      )}

      {/* Results count and sorting */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-ink-secondary">
          Znaleziono {pagination.total.toLocaleString('pl-PL')} ogłoszeń
        </div>
        <div className="text-sm text-ink-secondary">Sortuj: Termin składania ↑</div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-danger/5 border border-danger/20 rounded-md p-4">
          <p className="text-danger">Błąd podczas ładowania danych: {error}</p>
        </div>
      )}

      {/* Tenders Grid */}
      {!loading && !error && (
        <>
          {tenders.length === 0 ? (
            <div className="bg-surface border border-border rounded-lg p-8 text-center">
              <p className="text-ink-secondary">Brak ogłoszeń spełniających kryteria wyszukiwania.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {tenders.map((tender) => (
                <OpenTenderCard
                  key={tender.id}
                  tender={tender}
                  onViewDetail={(id) =>
                    onViewNotice ? onViewNotice(id) : navigate(`/tenders/${id}`)
                  }
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {tenders.length > 0 && (
            <Pagination
              currentPage={pagination.page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  )
}
